import { useMemo, useState } from 'react';
import {
  Box,
  Pagination,
  PaginationItem,
  Stack,
  SvgIcon,
  styled
} from '@mui/material';
import Input from '@/components/Input';
import InputAdornment from '@/components/InputAdornment';
import useResponsive from '@/hooks/useResponsive';
import Typography from '@/components/Typography';
import chunk from 'lodash/chunk';
import { ArrowLeft2, ArrowRight2 } from 'iconsax-react';
import map from 'lodash/map';
import find from 'lodash/find';
import useGetCrypto, {
  CryptoType,
  ascending,
  descending
} from '@/hooks/useGetCrypto';
import Row from './Row';
import handleSearch from '@/utils/handleSearch';
import CustomColumn, { Column, sortType } from './Column';
import Info from '../../../public/json/shitcoin/info.json';
import { useGetAllMarketInfoQuery } from '@/services/api';
import { sourceType } from '@/hooks/useLiveCrypto';

type Row = string | JSX.Element;

const Header = styled(Box)(({ theme }) => ({
  gap: '4px',
  height: 70,
  display: 'grid',
  borderRadius: 8,
  backgroundColor: '#1D2126',
  padding: theme.spacing(2, 6)
}));

const CustomRow = styled(Box)(({ theme }) => ({
  height: 68,
  gap: '4px',
  display: 'grid',
  borderRadius: 8,
  backgroundColor: '#1D2126',
  padding: theme.spacing(3, 6),
  [theme.breakpoints.down('lg')]: {
    padding: theme.spacing(3)
  }
}));

const PAGE_LIMIT = 15;
const ShitcoinTable = () => {
  const { matches } = useResponsive();
  const [searchQuery, setSearchQuery] = useState('');
  const [page, setPage] = useState(1);
  const [sort, setSort] = useState<{ value: sortType; key: string } | null>(
    null
  );

  const symbolFilter = map(
    Info.list,
    item =>
      `${item.base.toString().toUpperCase()}${item.quote
        .toString()
        .toUpperCase()}`
  );
  const { data: allMarketInfo } = useGetAllMarketInfoQuery();
  const { shitcoinTablePrice } = useGetCrypto(
    {
      shitcoinTable: true
    },
    'getCurrentList',
    symbolFilter
  );

  const list = useMemo(() => {
    const merged = map(shitcoinTablePrice, item => {
      const marketInfo = find(
        allMarketInfo?.data || [],
        mi => mi.curr_a === item.base && mi.curr_b === 'USDT'
      );
      const itemSource: sourceType =
        (find(Info.list, cr => cr.base === item.base && cr.quote === item.quote)
          ?.source as sourceType) || 'otc';
      const itemSourceKey = itemSource === 'otc' ? 'midPrice' : 'price';
      return {
        ...item,
        marketInfo,
        sort: {
          marketcap: marketInfo?.marketcap || 0,
          priceChange: item[itemSource]?.priceChange,
          volume: marketInfo?.vol_b || 0,
          closePrice: item[itemSource]?.[itemSourceKey]
        }
      };
    });
    return merged;
  }, [allMarketInfo]);

  const sortedList = useMemo(() => {
    const sortMap = { ascending, descending };
    const newList = sort ? sortMap[sort.value](list, sort.key, 'sort') : list;
    return newList;
  }, [sort]);

  const areas = matches({
    xs: "'crypto closePrice changePrice'",
    lg: "'crypto closePrice volume changePrice marketcap chart action'"
  });
  const columns = matches({
    xs: xsColumns,
    lg: lgColumns
  });

  const gridTemplateColumns = columns.map((c: Column) => c.span).join(' ');

  const handleSearchInput = (event: React.ChangeEvent<HTMLInputElement>) => {
    setPage(1);
    setSearchQuery(event.target.value);
  };

  const handlePagination = (
    event: React.ChangeEvent<unknown>,
    value: number
  ) => {
    setPage(value);
  };

  const newList = useMemo(
    () => chunk(handleSearch(sortedList, searchQuery), PAGE_LIMIT) || [],
    [sortedList, searchQuery, sort]
  );

  const hasItem = newList.length > 0;

  return (
    <Stack gap={1.5} mb={{ xs: 13, lg: 18 }}>
      <Stack
        gap={2}
        width="100%"
        mb={{ xs: 0, lg: 1.5 }}
        flexDirection={{ xs: 'column', lg: 'row' }}
        alignItems={{ xs: 'center', lg: 'center' }}
        justifyContent={{ xs: 'start', lg: 'space-between' }}
      >
        <Typography
          textAlign="center"
          variant={matches({ md: 'subtitle', lg: 'h6' })}
          component="p"
          weight="bold"
          color="textColor.A30"
        >
          رمزارزهای انفجاری موجود در رابکس ...
        </Typography>
        <Input
          fullWidth={matches({ xs: true, lg: false })}
          value={searchQuery}
          sx={{
            width: matches({ md: '100%', lg: 422 }),
            borderRadius: 3,
            height: matches({ md: 'unset', lg: 52 }),
            background: '#1D2126',
            '& .MuiInputBase-input.MuiInput-input': {
              color: '#CFD6E4'
            },
            '&:hover, &.Mui-focused': {
              border: 'none',
              background: '#1D2126'
            },
            border: 'none'
          }}
          placeholder="جست‌و‌جوی بازار ..."
          onChange={handleSearchInput}
          startAdornment={
            <InputAdornment position="start">
              <SvgIcon
                sx={theme => ({
                  fontSize: '24px',
                  color: '#EFF2F5'
                })}
              >
                <path
                  d="M9.58268 17.5013C13.9549 17.5013 17.4993 13.9569 17.4993 9.58464C17.4993 5.21238 13.9549 1.66797 9.58268 1.66797C5.21043 1.66797 1.66602 5.21238 1.66602 9.58464C1.66602 13.9569 5.21043 17.5013 9.58268 17.5013Z"
                  stroke="currentColor"
                  fill="transparent"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M18.3327 18.3346L16.666 16.668"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </SvgIcon>
            </InputAdornment>
          }
          {...(!!searchQuery && {
            endAdornment: (
              <InputAdornment
                position="end"
                sx={{ cursor: 'pointer' }}
                onClick={() => setSearchQuery('')}
              >
                <SvgIcon
                  sx={theme => ({
                    fontSize: '16px',
                    color: '#EFF2F5'
                  })}
                >
                  <path
                    d="M18 6L6 18"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M6 6L18 18"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </SvgIcon>
              </InputAdornment>
            )
          })}
          disableUnderline
        />
      </Stack>
      <Header style={{ gridTemplateColumns }}>
        {columns.map((column: Column, i: number) => (
          <CustomColumn
            key={i}
            title={column.title}
            secondaryTitle={column.secondaryTitle}
            align={column.align}
            padding={column.padding}
            sortKey={column.sortKey}
            onClick={val => setSort(val)}
          />
        ))}
      </Header>
      <Stack gap={1}>
        {(hasItem ? newList[page - 1] : []).map((item: CryptoType) => {
          const itemSource: sourceType =
            (find(
              Info.list,
              cr => cr.base === item.base && cr.quote === item.quote
            )?.source as sourceType) || 'otc';
          const itemSourceKey = itemSource === 'otc' ? 'midPrice' : 'price';
          return (
            <CustomRow
              key={item[itemSource]?.id}
              style={{
                gridTemplateColumns,
                gridTemplateAreas: areas ? areas : 'unset'
              }}
            >
              <Row
                pingPong={false}
                source={itemSource}
                symbol={item.symbol}
                {...matches({
                  xs: {
                    closePrice: item[itemSource]?.[itemSourceKey] || '-',
                    changePrice: item[itemSource]?.priceChange,
                    crypto: {
                      logo: item.logoUrl,
                      nameFa: item.baseFa,
                      symbol: item.base
                    },
                    onClickRedirect: `/price/${item.base}`
                  },
                  lg: {
                    crypto: {
                      logo: item.logoUrl,
                      nameFa: item.baseFa,
                      symbol: item.base
                    },
                    closePrice: item[itemSource]?.[itemSourceKey] || '-',
                    volume: item.marketInfo?.vol_b || '-',
                    marketcap: item.marketInfo?.marketcap || '-',
                    changePrice: item[itemSource]?.priceChange,
                    chart: item.chartUrl,
                    onClickRedirect: `/price/${item.base}`,
                    button: {
                      text: 'معامله',
                      href: 'https://p.rabex.ir/'
                    }
                  }
                })}
              />
            </CustomRow>
          );
        })}
      </Stack>
      <Stack alignItems="center" mt={matches({ md: 6, lg: 18 })}>
        {hasItem && (
          <Pagination
            renderItem={item => (
              <PaginationItem
                sx={{
                  color: '#A6B0C3',
                  '&.Mui-selected': {
                    boxShadow:
                      '0px -2px 8px rgb(0 0 0 / 4%), 0px 16px 24px rgb(0 0 0 / 25%)'
                  }
                }}
                slots={{ previous: ArrowLeft2, next: ArrowRight2 }}
                {...item}
              />
            )}
            count={newList.length}
            color="primary"
            page={page}
            onChange={handlePagination}
          />
        )}
      </Stack>
    </Stack>
  );
};

export default ShitcoinTable;

const xsColumns = [
  {
    title: 'ارز',
    align: 'start',
    padding: '0 0 0 24px',
    span: '2fr'
  },
  {
    title: 'آخرین قیمت',
    secondaryTitle: '(تومان)',
    align: 'start',
    sortKey: 'closePrice',
    span: '2fr'
  },
  {
    title: 'تغییرات',
    secondaryTitle: '(۲۴ ساعته)',
    align: 'end',
    sortKey: 'priceChange',
    span: '2fr'
  }
];

const lgColumns = [
  {
    title: 'ارز',
    span: '2.5fr',
    padding: '0 0 0 40px',
    align: 'start'
  },
  {
    title: 'آخرین قیمت',
    secondaryTitle: '(تومان)',
    span: '2.5fr',
    align: 'start',
    sortKey: 'closePrice'
  },
  {
    title: 'حجم معاملات',
    span: '2fr',
    align: 'start',
    sortKey: 'volume'
  },
  {
    title: 'تغییرات',
    secondaryTitle: '(۲۴ ساعته)',
    span: '2fr',
    align: 'start',
    sortKey: 'priceChange'
  },
  {
    title: 'مارکت کپ',
    span: '2fr',
    align: 'start',
    sortKey: 'marketcap'
  },
  {
    title: '',
    span: '2fr'
  },
  {
    title: '',
    align: 'end',
    span: '2fr'
  }
];
